import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Plus, Minus, Search, Archive, ArchiveRestore, PackagePlus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

export default function Inventory() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState(null);
  const [lowOnly, setLowOnly] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/inventory?include_archived=${showArchived}`);
    setItems(data);
  };
  useEffect(() => { load(); const id = setInterval(load, 6000); return () => clearInterval(id); }, [showArchived]);

  const categories = useMemo(() => Array.from(new Set(items.map((i) => i.category).filter(Boolean))).sort(), [items]);
  const filtered = useMemo(() => items.filter((it) => {
    if (category && it.category !== category) return false;
    if (lowOnly && (it.qty ?? 0) > (it.reorder ?? 0)) return false;
    if (q) {
      const hay = `${it.item} ${it.category} ${it.shelf}`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  }), [items, category, q, lowOnly]);

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach((it) => { (map[it.category || "Uncategorized"] = map[it.category || "Uncategorized"] || []).push(it); });
    return map;
  }, [filtered]);

  const isAdmin = user?.role === "admin";

  const adjust = async (id, delta) => {
    try { await api.post(`/inventory/${id}/adjust`, { delta }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const onArchive = async (it) => {
    try {
      if (it.archived) await api.post(`/inventory/${it.id}/unarchive`);
      else await api.post(`/inventory/${it.id}/archive`);
      toast.success(it.archived ? "Restored" : "Archived");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="inventory-page">
      <div className="flex flex-wrap items-end gap-3 justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Inventory</h1>
          <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mt-1">
            {filtered.length} items · {items.filter((i) => (i.qty ?? 0) <= (i.reorder ?? 0)).length} low stock
          </p>
        </div>
        <div className="flex gap-2">
          <label className="flex items-center gap-2 text-xs font-mono uppercase text-zinc-400">
            <input type="checkbox" checked={lowOnly} onChange={(e) => setLowOnly(e.target.checked)} data-testid="low-stock-toggle" className="accent-red-500" />
            Low Stock
          </label>
          <label className="flex items-center gap-2 text-xs font-mono uppercase text-zinc-400">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="accent-red-500" />
            Archived
          </label>
          {isAdmin && (
            <button onClick={() => setAddOpen(true)} data-testid="add-inventory-btn"
              className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 bg-red-600 hover:bg-red-500 rounded-sm transition-colors flex items-center gap-1.5">
              <PackagePlus className="h-3 w-3" /> Add Item
            </button>
          )}
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search items…" data-testid="inventory-search"
          className="w-full bg-zinc-900 border border-zinc-800 focus:border-red-500 focus:ring-2 focus:ring-red-500/30 rounded-sm pl-9 pr-3 py-2 text-sm font-mono outline-none transition-colors" />
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar">
        <Chip active={category == null} onClick={() => setCategory(null)}>All</Chip>
        {categories.map((c) => <Chip key={c} active={category === c} onClick={() => setCategory(c)}>{c}</Chip>)}
      </div>

      <div className="space-y-6">
        {Object.entries(grouped).map(([cat, arr]) => (
          <div key={cat}>
            <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 border-b border-zinc-800 pb-2 mb-2">{cat}</div>
            <ul className="divide-y divide-zinc-800 border border-zinc-800 rounded-sm">
              {arr.map((it) => {
                const low = (it.qty ?? 0) <= (it.reorder ?? 0);
                return (
                  <li key={it.id} className={`p-3 flex items-center justify-between gap-3 ${it.archived ? "opacity-60" : ""}`} data-testid={`inv-item-${it.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm text-zinc-100 truncate">{it.item}</div>
                      <div className="text-[11px] font-mono text-zinc-500 uppercase tracking-wide flex items-center gap-2">
                        <span>{it.shelf || "—"}</span>
                        <span>· Reorder @ {it.reorder}</span>
                        {low && !it.archived && <span className="text-red-400 border border-red-500/30 bg-red-500/10 px-1.5 py-0.5 rounded-sm">LOW</span>}
                        {it.archived && <span className="text-zinc-500 border border-zinc-700 px-1.5 py-0.5 rounded-sm">ARCHIVED</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isAdmin && (
                        <button onClick={() => adjust(it.id, -1)} disabled={it.qty <= 0 || it.archived} data-testid={`inv-decr-${it.id}`}
                          className="h-8 w-8 border border-zinc-700 hover:bg-zinc-800 disabled:opacity-40 rounded-sm flex items-center justify-center transition-colors">
                          <Minus className="h-3 w-3" />
                        </button>
                      )}
                      <div className="w-14 text-center font-mono text-lg text-zinc-100" data-testid={`inv-qty-${it.id}`}>{it.qty ?? 0}</div>
                      {isAdmin && (
                        <button onClick={() => adjust(it.id, 1)} disabled={it.archived} data-testid={`inv-incr-${it.id}`}
                          className="h-8 w-8 border border-zinc-700 hover:bg-zinc-800 disabled:opacity-40 rounded-sm flex items-center justify-center transition-colors">
                          <Plus className="h-3 w-3" />
                        </button>
                      )}
                      {isAdmin && (
                        <button onClick={() => onArchive(it)} data-testid={`inv-archive-${it.id}`}
                          className="h-8 w-8 border border-zinc-700 hover:bg-zinc-800 rounded-sm flex items-center justify-center transition-colors ml-1">
                          {it.archived ? <ArchiveRestore className="h-3 w-3" /> : <Archive className="h-3 w-3" />}
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
        {filtered.length === 0 && <div className="text-center text-sm text-zinc-500 py-8">No items found.</div>}
      </div>

      {addOpen && <AddItemDialog onClose={() => { setAddOpen(false); load(); }} categories={categories} />}
    </div>
  );
}

function Chip({ active, onClick, children }) {
  return (
    <button onClick={onClick} className={`shrink-0 text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded-full border transition-colors ${
      active ? "bg-zinc-100 text-zinc-900 border-zinc-100" : "bg-zinc-900 border-zinc-800 text-zinc-300 hover:bg-zinc-800"
    }`}>{children}</button>
  );
}

function AddItemDialog({ onClose, categories }) {
  const [form, setForm] = useState({ category: "", item: "", shelf: "", qty: 0, reorder: 0 });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.item || !form.category) { toast.error("Item and category required"); return; }
    setBusy(true);
    try {
      await api.post("/inventory", { ...form, qty: Number(form.qty) || 0, reorder: Number(form.reorder) || 0 });
      toast.success("Item added");
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
        <DialogHeader><DialogTitle>Add Inventory Item</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Item" value={form.item} onChange={(v) => setForm({ ...form, item: v })} testId="add-inv-item" />
          <Field label="Category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} list={categories} testId="add-inv-category" />
          <Field label="Shelf" value={form.shelf} onChange={(v) => setForm({ ...form, shelf: v })} testId="add-inv-shelf" />
          <div className="grid grid-cols-2 gap-2">
            <Field label="Qty" type="number" value={form.qty} onChange={(v) => setForm({ ...form, qty: v })} testId="add-inv-qty" />
            <Field label="Reorder" type="number" value={form.reorder} onChange={(v) => setForm({ ...form, reorder: v })} testId="add-inv-reorder" />
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="text-xs font-mono uppercase px-3 py-1.5 border border-zinc-700 rounded-sm hover:bg-zinc-800 transition-colors">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="add-inv-submit"
            className="text-xs font-mono uppercase px-3 py-1.5 bg-red-600 hover:bg-red-500 rounded-sm transition-colors">Add</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, type = "text", list, testId }) {
  return (
    <div>
      <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 block mb-1">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} list={list ? `${testId}-list` : undefined} data-testid={testId}
        className="w-full bg-zinc-900 border border-zinc-800 focus:border-red-500 rounded-sm px-2 py-1.5 font-mono text-sm outline-none" />
      {list && <datalist id={`${testId}-list`}>{list.map((o) => <option key={o} value={o} />)}</datalist>}
    </div>
  );
}
